import { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from 'context/AuthContext';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { useMsal } from '@azure/msal-react';
import { getAzureToken } from 'sso/azure/azure-api';
import { BiDesktop } from 'react-icons/bi';
import { removeStorageItem, setStorageItem } from 'utils/storage';
import { exchangeSSOToken } from 'services/auth.service';

import LogoReconheca from 'assets/img/rebrand/reconheca-logo.png';
import './styles.scss';

const PETAL_COLORS = ['#e0629b', '#d64f8a', '#ef6f8f', '#f28fb0', '#c94f86'];

/** Contorno orgânico concêntrico (marca "semente"), decorativo. */
function LoginContour() {
  const blob =
    'M300 60C420 60 520 130 560 250C600 370 560 470 470 540C380 610 250 620 160 560C70 500 30 380 60 260C90 150 180 60 300 60Z';
  const rings = [1, 0.86, 0.72, 0.58, 0.44, 0.3];
  return (
    <svg className="rb-login__contour" viewBox="0 0 620 640" preserveAspectRatio="xMidYMid meet" fill="none" aria-hidden="true">
      <g transform="translate(310 330)">
        {rings.map((s, i) => (
          <path
            key={i}
            d={blob}
            transform={`translate(-310 -330) scale(${s}) translate(${(310 * (1 - s)) / s} ${(330 * (1 - s)) / s})`}
            stroke="var(--blue-primary)"
            strokeWidth={1.6 / s}
            strokeOpacity={0.22 + i * 0.06}
          />
        ))}
      </g>
    </svg>
  );
}

/** Pétalas caindo — decorativo, posições fixas para não depender de random no render. */
function LoginPetals() {
  const petals = [
    { left: '8%', size: 12, dur: '9s', delay: '-2s' },
    { left: '22%', size: 9, dur: '11s', delay: '-6s' },
    { left: '35%', size: 15, dur: '8s', delay: '-1s' },
    { left: '48%', size: 10, dur: '12s', delay: '-8s' },
    { left: '61%', size: 13, dur: '10s', delay: '-4s' },
    { left: '74%', size: 8, dur: '13s', delay: '-9s' },
    { left: '86%', size: 14, dur: '9.5s', delay: '-3s' },
    { left: '94%', size: 10, dur: '11.5s', delay: '-7s' },
  ];
  return (
    <>
      {petals.map((p, i) => (
        <span
          key={i}
          className="rb-login__petal"
          style={{
            left: p.left,
            width: p.size,
            height: Math.round(p.size * 0.78),
            background: PETAL_COLORS[i % PETAL_COLORS.length],
            animationDuration: p.dur,
            animationDelay: p.delay,
          }}
        />
      ))}
    </>
  );
}

type Inputs = {
  user: string;
  pass: string;
};

export function Login() {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  const [invalidUser, setInvalidUser] = useState(false);
  const { loginWithToken, login } = useContext(AuthContext);
  const [useSSO, setUseSSO] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>();
  const onSubmit: SubmitHandler<Inputs> = async data => {
    if (data.user && data.pass) {
      await doLogin(data.user, data.pass);
    }
  };

  function loginSSO() {
    setLoading(true);
    setUseSSO(true);
    setInvalidUser(false);
  }

  async function handleLoginSSO(response: any) {
    // console.log('handleLoginSSO', response);

    try {
      const password = await getAzureToken();
      const username = response.userPrincipalName;

      if (username && password) {
        await login({ username, password });
        navigate('/');
      }
    } catch (e) {
      setLoading(false);
      setInvalidUser(true);
      setUseSSO(false);
    }
  }

  const doLogin = useCallback(
    async (username: string, password: string) => {
      try {
        setLoading(true);
        setErrorMessage('');
        await login({ username, password });
        navigate('/');
      } catch (e: any) {
        setLoading(false);
        setInvalidUser(true);

        if (typeof e.message === 'string' && e.message === 'password incorrect for player') {
          setErrorMessage('O nome de usuário ou senha digitados estão incorretos. Por favor, tente novamente.');
        } else {
          setErrorMessage('Ocorreu um erro com sua solicitação. Por favor, tente novamente.');
        }
      }
    },
    [login, navigate],
  );

  useEffect(() => {
    async function doLoginWithToken(token: string) {
      try {
        await loginWithToken(token);
        navigate('/');
      } catch (e) {
        setLoading(false);
        setInvalidUser(true);
      }
    }

    async function doLoginWithSSOToken(ssoToken: string) {
      try {
        const { access_token } = await exchangeSSOToken(ssoToken);
        await loginWithToken(access_token);
        navigate('/');
      } catch (e) {
        setLoading(false);
        setInvalidUser(true);
      }
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const agent = params.get('agent');
    const secret = params.get('secret');
    const tokenItem = params.get('tokenItem');
    const ssoToken = params.get('sso_token');

    if (ssoToken) {
      setLoading(true);

      params.delete('sso_token');
      const remainingQuery = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (remainingQuery ? `?${remainingQuery}` : ''));

      doLoginWithSSOToken(ssoToken);
      removeStorageItem('readOnly');
    } else if (token) {
      setLoading(true);
      doLoginWithToken(token);
      removeStorageItem('readOnly');
    } else if (agent && secret) {
      setLoading(true);
      doLogin(agent, secret);
      removeStorageItem('readOnly');
    } else if (tokenItem) {
      setLoading(true);
      const decodedToken = atob(tokenItem);
      const tokenParts = decodedToken.split(':');
      const id = tokenParts[0];
      const password = tokenParts[1];

      setStorageItem('readOnly', 'true');
      doLogin(id, password);
    } else {
      // setUseSSO(true);
      setInvalidUser(true);
    }
  }, [loginWithToken, login, navigate, doLogin]);

  return (
    <div className="rb-login">
      <LoginContour />
      <LoginPetals />

      <form className="rb-login__card" onSubmit={handleSubmit(onSubmit)}>
        <img className="rb-login__logo" src={LogoReconheca} alt="Reconheça BRB" />

        <p className="rb-login__lead">A participação no Reconheça BRB é voluntária.</p>
        <p className="rb-login__sub">
          Para participar, leia o{' '}
          <Link to="/regulamento">regulamento</Link> e manifeste sua concordância com os termos da ação.
        </p>

        {invalidUser && (
          <>
            <div className="rb-login__field">
              <label htmlFor="user" className="rb-login__label">
                Usuário
              </label>
              <input
                type="text"
                id="user"
                className="rb-login__input"
                placeholder="Digite seu usuário"
                {...register('user', { required: true })}
                disabled={loading}
              />
            </div>

            <div className="rb-login__field">
              <label htmlFor="password" className="rb-login__label">
                Senha
              </label>
              <input
                type="password"
                id="password"
                className="rb-login__input"
                placeholder="Digite sua senha"
                {...register('pass', { required: true })}
                disabled={loading}
              />
            </div>

            <button className="rb-login__enter" type="submit" disabled={loading}>
              {loading ? (
                'Carregando...'
              ) : (
                <>
                  <BiDesktop size={18} /> Entrar
                </>
              )}
            </button>

            {!!errorMessage && <p className="rb-login__error">{errorMessage}</p>}
          </>
        )}

        {loading && !invalidUser && <p className="rb-login__loading">Carregando...</p>}
      </form>
    </div>
  );
}
